import { useConfiguration } from '@components/router/session-context';

export function imageUrl(
  config: { cdn: string; videoCdn: string },
  path?: string,
  opts?: { isVideoBucket?: boolean },
) {
  const base = opts?.isVideoBucket ? config.videoCdn : config.cdn;
  return path && `${base}/${path}`;
}

/**
 * Resolve the CDN URL for an uploaded file — image, audio, video, or document.
 * Pass `isVideoBucket` for files that live in the dedicated video bucket so the
 * URL routes to the video CDN.
 */
export function useMediaUrl(
  path?: string,
  opts?: { maxWidth?: number; isVideoBucket?: boolean; version?: string },
) {
  const config = useConfiguration();
  if (!path || path.includes('://')) {
    return path;
  }
  if (!config || !path) {
    return;
  }
  if (path.includes('/profilepic')) {
    return path;
  }
  const url = imageUrl(config, path, opts);
  // `version` is a stable per-content cache-buster (the file's transcodedAt). A
  // re-transcode overwrites the CMAF output in place, so the url is unchanged and
  // the browser keeps serving its cached (stale) copy even after the CDN edge is
  // purged — purge can't reach an already-populated browser cache. Changing `?v=`
  // makes the new bytes a fresh url, hence a guaranteed cache miss.
  const params: string[] = [];
  if (opts?.maxWidth) {
    params.push(`width=${opts.maxWidth}`);
  }
  if (opts?.version) {
    params.push(`v=${encodeURIComponent(opts.version)}`);
  }
  return url + (params.length ? `?${params.join('&')}` : '');
}

/**
 * Resolve the CDN URL for an image-only field (course/brand images, avatars,
 * video posters, thumbnails). Same resolution as `useMediaUrl`; the distinct
 * name documents that the field never holds audio/video.
 */
export function useImageUrl(path?: string, opts?: { maxWidth?: number; isVideoBucket?: boolean }) {
  return useMediaUrl(path, opts);
}
